import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthProvider';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Sparkles } from 'lucide-react';

interface AIAssistantComingSoonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AIAssistantComingSoonModal: React.FC<AIAssistantComingSoonModalProps> = ({ open, onOpenChange }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNotifyMe = async () => {
    if (!user) {
      toast({
        title: t('aiAssistant.notifyMeError') || 'Error',
        description: t('aiAssistant.notifyMeErrorDesc') || 'Please sign in to get notified.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('feature_interests')
        .upsert(
          {
            user_id: user.id,
            feature_name: 'aiAssistant',
            notified: false,
          },
          {
            onConflict: 'user_id,feature_name',
          }
        );

      if (error) throw error;

      toast({
        title: t('aiAssistant.notifyMeSuccess') || 'Success',
        description: t('aiAssistant.notifyMeSuccessDesc') || 'We will notify you when the AI Assistant is available.',
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save feature interest:', error);
      toast({
        title: t('aiAssistant.notifyMeError') || 'Error',
        description: error instanceof Error ? error.message : t('aiAssistant.notifyMeErrorDesc') || 'Failed to save your interest. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const exampleQuestions = [
    t('aiAssistant.question1') || 'When did we last change the oil — and when is the next one actually due?',
    t('aiAssistant.question3') || 'What maintenance should I expect next based on my mileage?',
    t('aiAssistant.question4') || 'Can you summarize everything I need to tell the mechanic?',
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-center mb-2">
            <div className="bg-[#e7fe52]/30 rounded-full p-3">
              <Sparkles className="w-6 h-6 text-[#a2b800]" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            {t('aiAssistant.comingSoon') || 'AI Assistant Coming Soon'}
          </DialogTitle>
          <DialogDescription className="text-center text-sm mt-1.5">
            {t('aiAssistant.comingSoonDescription') || 'Imagine a tool like ChatGPT — but one that already knows your car\'s details, issues, tasks, and full history.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          <h3 className="font-semibold text-base">
            {t('aiAssistant.getInstantAnswers') || 'Get instant answers to questions like:'}
          </h3>
          <div className="grid grid-cols-1 gap-2">
            {exampleQuestions.map((question, index) => (
              <div 
                key={index} 
                className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors shadow-md"
              >
                <p className="text-sm text-gray-700 leading-relaxed">{question}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 mt-4">
          <Button
            onClick={handleNotifyMe}
            disabled={isSubmitting}
            className="w-full bg-lime text-black hover:bg-[#d4e848]"
          >
            {isSubmitting
              ? (t('actions.processing') || 'Processing...')
              : (t('aiAssistant.notifyMe') || 'Notify me')}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            {t('aiAssistant.back') || 'Back'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIAssistantComingSoonModal;

